import { useEffect, useState, useRef } from 'react'
import { Building2, MapPin, Bell, CreditCard, Plus, X, Edit2, Trash2, Check, AlertCircle, Save } from 'lucide-react'
import api from '../../services/api'

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']
const SITE_TYPES = ['office', 'warehouse', 'field', 'construction', 'manufacturing', 'other']
const INDUSTRY_OPTIONS = ['Construction', 'Mining', 'Oil & Gas', 'Manufacturing', 'Transport & Logistics', 'Utilities', 'Healthcare', 'Government', 'Agriculture', 'Other']

type Tab = 'organisation' | 'sites' | 'notifications' | 'billing'

export default function AdminSettings() {
  const [tab, setTab] = useState<Tab>('organisation')
  const [tenant, setTenant] = useState<any>(null)
  const [sites, setSites] = useState<any[]>([])
  const [contacts, setContacts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState('')

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  const load = async () => {
    setLoading(true)
    try {
      const [orgRes, sitesRes, notifRes] = await Promise.all([
        api.get('/organisation/profile'),
        api.get('/organisation/sites'),
        api.get('/notifications/contacts').catch(() => ({ data: [] })),
      ])
      setTenant(orgRes.data.tenant)
      setSites(sitesRes.data || [])
      setContacts(notifRes.data?.data || notifRes.data || [])
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  if (loading) return <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: 'organisation', label: 'Organisation', icon: Building2 },
    { id: 'sites', label: 'Sites', icon: MapPin },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'billing', label: 'Billing', icon: CreditCard },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Admin Settings</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Manage organisation configuration and system settings</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'var(--border)' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2"
            style={{
              borderBottomColor: tab === t.id ? 'var(--accent)' : 'transparent',
              color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${tab === t.id ? 'var(--accent)' : 'transparent'}`,
              cursor: 'pointer',
            }}
          >
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'organisation' && <OrganisationTab tenant={tenant} onSaved={() => { load(); showToast('Saved') }} />}
      {tab === 'sites' && <SitesTab sites={sites} onChanged={() => { load(); showToast('Saved') }} />}
      {tab === 'notifications' && <NotificationsTab contacts={contacts} onChanged={() => { load(); showToast('Saved') }} />}
      {tab === 'billing' && <BillingTab tenant={tenant} />}

      {toast && (
        <div className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white" style={{ background: 'var(--green, #16a34a)', zIndex: 9999 }}>
          <Check size={16} />{toast}
        </div>
      )}
    </div>
  )
}

// ─── Organisation Tab ────────────────────────────────────────────────────────

function OrganisationTab({ tenant, onSaved }: { tenant: any; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: tenant?.name || '',
    abn: tenant?.abn || '',
    industry: tenant?.industry || '',
    primary_jurisdiction: tenant?.primary_jurisdiction || '',
    jurisdictions: JSON.parse(tenant?.jurisdictions || '[]') as string[],
    authorised_signatory: tenant?.authorised_signatory || '',
    primary_colour: tenant?.primary_colour || '#0ea5e9',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const toggleJurisdiction = (state: string) => {
    setForm(f => ({
      ...f,
      jurisdictions: f.jurisdictions.includes(state)
        ? f.jurisdictions.filter(j => j !== state)
        : [...f.jurisdictions, state],
    }))
  }

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async () => {
      try {
        await api.patch('/organisation/branding', { logo_base64: reader.result as string })
        onSaved()
      } catch {}
    }
    reader.readAsDataURL(file)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      await Promise.all([
        api.patch('/organisation/tenant', {
          name: form.name,
          abn: form.abn,
          industry: form.industry,
          primary_jurisdiction: form.primary_jurisdiction,
          jurisdictions: form.jurisdictions,
        }),
        api.patch('/organisation/branding', {
          authorised_signatory: form.authorised_signatory,
          primary_colour: form.primary_colour,
        }),
      ])
      onSaved()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save')
    }
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      <div className="card p-6 space-y-5">
        <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Company Details</h2>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Company Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>ABN</label>
            <input value={form.abn} onChange={e => setForm(f => ({ ...f, abn: e.target.value }))} placeholder="12 345 678 901" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Industry</label>
            <select value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}>
              <option value="">— Select —</option>
              {INDUSTRY_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Primary Jurisdiction</label>
            <select value={form.primary_jurisdiction} onChange={e => setForm(f => ({ ...f, primary_jurisdiction: e.target.value }))}>
              <option value="">— Select —</option>
              {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Authorised Signatory</label>
            <input value={form.authorised_signatory} onChange={e => setForm(f => ({ ...f, authorised_signatory: e.target.value }))} placeholder="Full name for reports" />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Operating Jurisdictions</label>
          <div className="flex flex-wrap gap-2">
            {AU_STATES.map(state => (
              <button
                key={state}
                type="button"
                onClick={() => toggleJurisdiction(state)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border transition-all"
                style={{
                  background: form.jurisdictions.includes(state) ? 'var(--accent)' : 'var(--bg)',
                  color: form.jurisdictions.includes(state) ? 'white' : 'var(--text-secondary)',
                  borderColor: form.jurisdictions.includes(state) ? 'var(--accent)' : 'var(--border)',
                  cursor: 'pointer',
                }}
              >
                {state}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card p-6 space-y-5">
        <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Branding</h2>
        <div className="flex items-center gap-6">
          {tenant?.logo_base64 ? (
            <img src={tenant.logo_base64} alt="Logo" className="h-16 w-auto object-contain rounded" />
          ) : (
            <div className="h-16 w-32 rounded border flex items-center justify-center text-xs" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>No logo</div>
          )}
          <div>
            <button type="button" className="btn btn-secondary text-sm" onClick={() => fileRef.current?.click()}>Upload Logo</button>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>PNG, SVG or JPG · Max 2MB</p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Brand Colour</label>
          <div className="flex items-center gap-3">
            <input type="color" value={form.primary_colour} onChange={e => setForm(f => ({ ...f, primary_colour: e.target.value }))} style={{ width: 44, height: 36, padding: 2, cursor: 'pointer' }} />
            <span className="text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>{form.primary_colour}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
          <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}
        </div>
      )}

      <button type="submit" className="btn btn-primary" disabled={saving}>
        <Save size={16} />{saving ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  )
}

// ─── Sites Tab ───────────────────────────────────────────────────────────────

function SitesTab({ sites, onChanged }: { sites: any[]; onChanged: () => void }) {
  const [showAdd, setShowAdd] = useState(false)
  const [editSite, setEditSite] = useState<any>(null)
  const [form, setForm] = useState({ name: '', address: '', state: '', postcode: '', site_type: 'office', headcount: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const resetForm = () => {
    setForm({ name: '', address: '', state: '', postcode: '', site_type: 'office', headcount: '' })
    setError('')
  }

  const openAdd = () => { resetForm(); setEditSite(null); setShowAdd(true) }
  const openEdit = (site: any) => {
    setForm({ name: site.name, address: site.address || '', state: site.state || '', postcode: site.postcode || '', site_type: site.site_type || 'office', headcount: site.headcount?.toString() || '' })
    setEditSite(site)
    setShowAdd(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name) { setError('Site name is required'); return }
    setSaving(true)
    setError('')
    const payload = {
      name: form.name,
      address: form.address || undefined,
      state: form.state || undefined,
      postcode: form.postcode || undefined,
      site_type: form.site_type,
      headcount: form.headcount ? parseInt(form.headcount) : undefined,
    }
    try {
      if (editSite) {
        await api.patch(`/organisation/sites/${editSite.id}`, payload)
      } else {
        await api.post('/organisation/sites', payload)
      }
      setShowAdd(false)
      onChanged()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save')
    }
    setSaving(false)
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Archive site "${name}"? It will no longer appear in dropdowns.`)) return
    try {
      await api.delete(`/organisation/sites/${id}`)
      onChanged()
    } catch {}
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sites.length} active sites</p>
        <button onClick={openAdd} className="btn btn-primary"><Plus size={16} /> Add Site</button>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Site Name</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Type</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>State</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Headcount</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {sites.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8" style={{ color: 'var(--text-muted)' }}>No sites configured</td></tr>
            ) : sites.map(s => (
              <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)' }}>{s.name}</td>
                <td className="px-4 py-3 text-xs capitalize" style={{ color: 'var(--text-secondary)' }}>{s.site_type || '—'}</td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{s.state || '—'}</td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{s.headcount || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openEdit(s)} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}><Edit2 size={13} /></button>
                    <button onClick={() => handleDelete(s.id, s.name)} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--red)' }}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-md rounded-2xl shadow-xl" style={{ background: 'var(--white)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>{editSite ? 'Edit Site' : 'Add Site'}</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Site Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Brisbane Depot" required />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Address</label>
                <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Example St" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>State</label>
                  <select value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}>
                    <option value="">—</option>
                    {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Postcode</label>
                  <input value={form.postcode} onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))} placeholder="4000" maxLength={4} />
                </div>
                <div className="col-span-1">
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Headcount</label>
                  <input type="number" min="0" value={form.headcount} onChange={e => setForm(f => ({ ...f, headcount: e.target.value }))} placeholder="0" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Site Type</label>
                <select value={form.site_type} onChange={e => setForm(f => ({ ...f, site_type: e.target.value }))}>
                  {SITE_TYPES.map(t => <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              {error && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editSite ? 'Save Changes' : 'Add Site'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Notifications Tab ────────────────────────────────────────────────────────

function NotificationsTab({ contacts, onChanged }: { contacts: any[]; onChanged: () => void }) {
  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        Notification contacts receive alerts for incidents, corrective actions, and permit escalations based on their assigned notification levels.
      </p>

      {contacts.length === 0 ? (
        <div className="card text-center py-12">
          <Bell size={36} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-medium" style={{ color: 'var(--text)' }}>No notification contacts configured</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Add contacts via the Notifications module</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Name</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Role</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Contact</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Notify Levels</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Methods</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c: any) => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)' }}>{c.name}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{c.role}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {c.email && <div>{c.email}</div>}
                    {c.mobile && <div>{c.mobile}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {c.notify_critical && <span className="badge badge-critical" style={{ fontSize: 10 }}>Critical</span>}
                      {c.notify_high && <span className="badge badge-high" style={{ fontSize: 10 }}>High</span>}
                      {c.notify_medium && <span className="badge badge-medium" style={{ fontSize: 10 }}>Medium</span>}
                      {c.notify_low && <span className="badge badge-low" style={{ fontSize: 10 }}>Low</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {[c.method_email && 'Email', c.method_sms && 'SMS'].filter(Boolean).join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        To manage notification contacts in detail, visit the <a href="/notifications" className="underline" style={{ color: 'var(--accent)' }}>Notifications module</a>.
      </p>
    </div>
  )
}

// ─── Billing Tab ─────────────────────────────────────────────────────────────

function BillingTab({ tenant }: { tenant: any }) {
  const PLAN_LABEL: Record<string, string> = {
    trial: 'Free Trial',
    standard: 'Standard',
    enterprise: 'Enterprise',
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="card p-6">
        <h2 className="font-semibold mb-4" style={{ color: 'var(--text)' }}>Current Plan</h2>
        <div className="flex items-center justify-between p-4 rounded-xl border" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
          <div>
            <div className="font-bold text-lg" style={{ color: 'var(--text)' }}>
              {PLAN_LABEL[tenant?.plan] || tenant?.plan || 'Trial'}
            </div>
            <div className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {tenant?.plan === 'trial' ? 'Upgrade to unlock all features' : 'Full platform access'}
            </div>
          </div>
          <span className={`badge ${tenant?.plan === 'enterprise' ? 'badge-critical' : tenant?.plan === 'standard' ? 'badge-high' : 'badge-medium'}`}>
            {PLAN_LABEL[tenant?.plan] || 'Trial'}
          </span>
        </div>
      </div>

      <div className="card p-6">
        <h2 className="font-semibold mb-4" style={{ color: 'var(--text)' }}>Plans</h2>
        <div className="grid grid-cols-2 gap-4">
          {[
            { plan: 'standard', label: 'Standard', price: '$199/mo', features: ['Up to 50 users', 'All core modules', 'Email support', 'AU data residency'] },
            { plan: 'enterprise', label: 'Enterprise', price: 'Custom', features: ['Unlimited users', 'All modules + API', 'Dedicated support', 'Custom integrations', 'SLA guarantee'] },
          ].map(p => (
            <div key={p.plan} className="border rounded-xl p-4" style={{ borderColor: 'var(--border)' }}>
              <div className="font-bold" style={{ color: 'var(--text)' }}>{p.label}</div>
              <div className="text-xl font-bold mt-1" style={{ color: 'var(--accent)' }}>{p.price}</div>
              <ul className="mt-3 space-y-1">
                {p.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <Check size={13} style={{ color: 'var(--green)' }} />{f}
                  </li>
                ))}
              </ul>
              <button className="btn btn-primary w-full mt-4 text-sm" disabled>
                {tenant?.plan === p.plan ? 'Current Plan' : 'Contact Sales'}
              </button>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
        Billing managed via Stripe · Contact support@hseqintelligence.com.au for enterprise pricing
      </p>
    </div>
  )
}
